import paymentStatus from '../../paymentStatus';
import { OtherString } from '../otherString';
import { PosProcessingItem } from './posProcessingItem';
import { VerificationItem } from './verificationItem';
import { z } from 'zod';

export type PaymentCodes = z.infer<typeof PaymentCodes>;
export type ParkingEan = z.infer<typeof ParkingEan>;
export type PaymentMethod = z.infer<typeof PaymentMethod>;
export type Payment = z.infer<typeof Payment>;
export type PaymentStatus = z.infer<typeof PaymentStatus>;
export type PaymentSummary = z.infer<typeof PaymentSummary>;
export type paymentSummaryBlock = z.infer<typeof paymentSummaryBlock>;
export type paymentSummaryDetail = z.infer<typeof paymentSummaryDetail>;
export type PaymentStatusString = z.infer<typeof PaymentStatusString>;
export type PaymentStatusEnum = z.infer<typeof PaymentStatusEnum>;
export type ExternalPayment = z.infer<typeof ExternalPayment>;

export const PaymentCodes = z.object({
    basketItemsCounts: z.record(z.string(), z.number()),
    shortCode: z.string().nullish(),
    type: z.string().nullish(),
    code: z.string().nullish(),
});

export const ParkingEan = z.object({
    shortCode: z.string().nullish(),
    type: z.string().nullish(),
    code: z.string().nullish(),
});

export const PaymentStatusEnum = z.nativeEnum(paymentStatus);
export const PaymentStatusString = z.enum([
    'PendingVerification',
    'Created',
    'Completed',
    'CustomerCancelled',
    'EmployeeCancelled',
    'PaymentProviderDecline',
    'EmployeeReset',
    'NewPaymentRequestCreated',
    'SystemCancelled',
    'PosBackchannelCancelled',
]);
export const PaymentStatus = z.union([PaymentStatusEnum, PaymentStatusString]);

export const PaymentMethod = z.enum(['cashier', 'creditcard', 'apple_google_pay', 'direct_debit', 'edeka-app']);

export const paymentSummaryDetail = z.object({
    text: z.string(),
    total: z.number(),
});

export const paymentSummaryBlock = z.object({
    total: z.number(),
    details: paymentSummaryDetail.array().nullish(),
});

export const PaymentSummary = z.object({
    originalTotal: z.number(),
    total: z.number(),
    empties: paymentSummaryBlock.nullish(),
    discount: paymentSummaryBlock.nullish(),
    partialPayment: paymentSummaryBlock.nullish(),
    receiptVouchers: paymentSummaryBlock.nullish(),
});

export const PaymentUser = z.object({
    userId: z.string().nullish(),
    deviceId: z.string().nullish(),
});
export type PaymentUser = z.infer<typeof PaymentUser>;

export const Payment = z.object({
    paymentRequestGuid: z.string(),
    sessionGuid: z.string(),
    userGuid: z.string().nullish(),
    sequence: z.number(),
    basketId: z.string().nullish(),
    posTransferItems: z.unknown().nullish(),
    paymentCodes: z.array(PaymentCodes).nullish(),
    verificationItems: z.array(VerificationItem).nullish(),
    fallbackIdentificationEan: z.string().nullish(),
    posInterfaceIdentificationEan: z.string().nullish(),
    parkingEan: ParkingEan.nullish(),
    total: z.number().nullish(),
    posTotal: z.number().nullish(),
    excludedFromPosTotal: z.number().nullish(),
    failedItemTotal: z.number().nullish(),
    easyTotal: z.number().nullish(),
    totalSrc: z.string().nullish(),
    count: z.number().nullish(),
    status: PaymentStatus.nullish(),
    requiredAge: z.number().nullish(),
    confirmedOn: z.coerce.date().nullish(),
    verifiedOn: z.coerce.date().nullish(),
    completedOn: z.coerce.date().nullish(),
    cashierLockedOn: z.coerce.date().nullish(),
    paymentMethod: PaymentMethod.or(OtherString).nullish(),
    customerCard: z.string().nullish(),
    employeeCard: z.string().nullish(),
    properties: z.record(z.string(), z.unknown()).nullish(),
    posInterfaceResult: z.unknown().nullish(),
    loginType: z.string().nullish(),
    customer: z
        .object({
            customerNumber: z.int().nullish(),
            labels: z.string().array().nullish(),
            userGuid: z.string().nullish(),
            userHash: z.string().nullish(),
        })
        .nullish(),
    customerAppPaymentState: z
        .object({
            paymentMethod: PaymentMethod,
            state: z.literal('failed').or(z.literal('processing')).or(z.literal('completed')),
            error: z
                .object({
                    code: z.number(),
                    message: z.string(),
                    exception: z.string().nullish(),
                    isTransient: z.boolean().nullish(),
                })
                .nullish(),
        })
        .nullish(),

    processingBy: PaymentUser.nullish(),
    postProcessingBy: PaymentUser.nullish(),
    locked: z.boolean().nullish(),
    mobileLocked: z.boolean().nullish(),
    cashierId: z.string().nullish(),
    correlationGuids: z.array(z.string()).nullish(),
    discountableTotal: z.number().nullish(),
    paymentTrackingSummary: z.unknown().nullish(),
    posRegistrationCompletedOn: z.union([z.coerce.date(), z.boolean()]).nullish(),
    posBackchannelCompletionReceivedOn: z.coerce.date().nullish(),
    requiresPosProcessingItems: z.array(PosProcessingItem).nullish(),
    completionSources: z.array(z.unknown()).nullish(),
    completionSource: z.string().nullish(),
    expressCashierWeights: z.array(z.unknown()).nullish(),
    images: z.unknown().nullish(),
    posPaymentBonId: z.string().nullish(),
    posExternalTransactionId: z.string().nullish(),
    requestCreatedQueueHops: z.unknown().nullish(),
    createdOn: z.coerce.date().nullish(),
    modifiedOn: z.coerce.date().nullish(),
    receiptVouchers: z.array(z.object({ text: z.string(), total: z.number() })).nullish(),
    retryableProcessPayment: z.boolean().nullish(),
    storeGuid: z.string().nullish(),
    cashierGuid: z.string().nullish(),
    isPosProcessing: z.boolean().nullish(),
    posProcessingType: z.string().nullish(),
    isMobile: z.boolean().nullish(),
});

export const ExternalPayment = z.object({
    success: z.boolean(),
    successOn: z.string().nullish(),
    completed: z.boolean(),
    completedOn: z.string().nullish(),
    accepted: z.boolean(),
    paymentMethod: z.string().nullish(),
});
